import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import Auth from "./auth";
import { signRequest } from "./signature";

const BASE_URL = import.meta.env.VITE_API_URL;
const SECRET_KEY = import.meta.env.VITE_SECRET_KEY;

export const HEADERS = {
  AUTH: {
    Accept: "*/*",
    "Content-Type": "application/json",
  },
  JSON: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  MULTIPART: {
    Accept: "application/json",
    "Content-Type": "multipart/form-data",
  },
  FORM: {
    Accept: "application/json",
    "Content-Type": "application/x-www-form-urlencoded",
  },
} as const;

let isRefreshing = false;
let refreshPromise: Promise<any> | null = null;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}> = [];

let isLoggedOut = false;

export const setLoggedOutState = (state: boolean) => {
  isLoggedOut = state;
};

const processQueue = (error: any = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve();
    }
  });

  failedQueue = [];
};

class HttpClient {
  private instance: AxiosInstance;

  constructor(config?: AxiosRequestConfig) {
    this.instance = axios.create({
      baseURL: BASE_URL,
      headers: {
        ...HEADERS.JSON,
        ...config?.headers,
      },
      withCredentials: true,
      timeout: 10000,
      ...config,
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    this.instance.interceptors.request.use(
      async (config) => {
        await signRequest(config, SECRET_KEY);

        if (config.data instanceof FormData) {
          config.headers["Content-Type"] = "multipart/form-data";
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (isLoggedOut && error.response?.status === 401) {
          return Promise.reject(new Error("User is logged out"));
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then(() => {
                return this.instance(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          isRefreshing = true;

          try {
            if (!refreshPromise) {
              refreshPromise = Auth.refresh();
            }

            const refreshResponse = await refreshPromise;

            if (!refreshResponse.success) {
              setLoggedOutState(true);
              processQueue(new Error("Refresh failed"));
              throw new Error("Refresh failed");
            }

            processQueue();

            return this.instance(originalRequest);
          } catch (refreshError) {
            setLoggedOutState(true);
            processQueue(refreshError);
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
            refreshPromise = null;
          }
        }
        return Promise.reject(error);
      }
    );
  }

  public withHeaders(headers: (typeof HEADERS)[keyof typeof HEADERS]) {
    return new HttpClient({ headers }).getInstance();
  }

  getInstance() {
    return this.instance;
  }
}

const http = new HttpClient().getInstance();
export default http;
